<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RealEstateFinanceBoletoFields from './RealEstateFinanceBoletoFields.vue';
import RealEstateFinanceBoletoFilePanel from './RealEstateFinanceBoletoFilePanel.vue';

const props = defineProps({
  loading: { type: Boolean, default: false },
  responsibility: { type: Object, default: null },
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'download', 'remove', 'save', 'upload']);
const { t } = useI18n();
const amount = ref('');
const issuedOn = ref('');
const sentOn = ref('');
const dueOn = ref('');
const boleto = computed(() => props.responsibility?.boleto || {});
const canManage = computed(
  () => props.responsibility?.acoes?.pode_enviar_boleto
);
const hasBoleto = computed(() => boleto.value.enviado);
const canSave = computed(
  () =>
    hasBoleto.value &&
    amount.value &&
    issuedOn.value &&
    sentOn.value &&
    dueOn.value
);

watch(
  [() => props.show, () => props.responsibility],
  ([show]) => {
    if (!show) return;
    amount.value = props.responsibility?.valor_boleto?.amount || '';
    issuedOn.value = props.responsibility?.data_emissao || '';
    sentOn.value = props.responsibility?.data_envio || '';
    dueOn.value = props.responsibility?.data_validade || '';
  },
  { immediate: true }
);

const save = () => {
  if (!canSave.value) return;
  emit('save', {
    valor_boleto: amount.value,
    data_emissao: issuedOn.value,
    data_envio: sentOn.value,
    data_validade: dueOn.value,
  });
};
</script>

<template>
  <LaisModal
    :show="show"
    :title="t('REAL_ESTATE_FINANCE.BOLETO.TITLE')"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <p class="text-sm text-lais-text-muted">
        {{ responsibility?.usuario_nome }}
      </p>
      <RealEstateFinanceBoletoFilePanel
        :boleto="boleto"
        :can-manage="canManage"
        :loading="loading"
        :show="show"
        @download="emit('download')"
        @remove="emit('remove')"
        @upload="emit('upload', $event)"
      />
      <RealEstateFinanceBoletoFields
        v-model:amount="amount"
        v-model:due-on="dueOn"
        v-model:issued-on="issuedOn"
        v-model:sent-on="sentOn"
        :disabled="!hasBoleto || !canManage"
      />
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          {{ t('REAL_ESTATE_FINANCE.ACTIONS.CLOSE') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="canManage"
          :disabled="!canSave"
          :is-loading="loading"
          @click="save"
        >
          {{ t('REAL_ESTATE_FINANCE.BOLETO.SAVE') }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
